import { useEffect, useState, useCallback } from "react";
import { Plus, X, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { DataTable, Badge } from "@/components/common/DataTable";
import { useAuth } from "@/context/AuthContext";

export default function DictionaryTab() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canCreate = isSuperAdmin || hasPermission("LANGUAGE.CREATE");
  const canApprove = isSuperAdmin || hasPermission("LANGUAGE.APPROVE");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ term: "", category: "", doNotTranslate: true });

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/v1/i18n/admin/dictionary", { params: { size: 300 } })).data.data.items); }
    catch { setItems([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (term) => {
    try {
      await api.post(`/v1/i18n/admin/dictionary/${term.id}/approve`);
      toast.success(`"${term.term}" approved`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const create = async () => {
    if (!form.term.trim()) { toast.error("Term is required"); return; }
    try {
      await api.post("/v1/i18n/admin/dictionary", { ...form, status: "draft", version: 0 });
      toast.success("Term added");
      setShowForm(false);
      setForm({ term: "", category: "", doNotTranslate: true });
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="dictionary-tab">
      <div className="flex justify-end mb-3">
        {canCreate && (
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 py-1.5" data-testid="add-term-btn">
            <Plus className="h-4 w-4" /> Add term
          </button>
        )}
      </div>
      <DataTable testid="dictionary-table" rows={items} loading={loading}
        columns={[
          { key: "term", header: "Term", render: (t) => <span className="font-medium text-foreground">{t.term}</span> },
          { key: "category", header: "Category", render: (t) => <span className="text-xs text-muted-foreground">{t.category || "—"}</span> },
          { key: "doNotTranslate", header: "Locked", render: (t) => t.doNotTranslate ? <span className="inline-flex items-center gap-1 text-xs text-amber-600"><Lock className="h-3 w-3" /> Verbatim</span> : <span className="text-xs text-muted-foreground">Translatable</span> },
          { key: "status", header: "Status", render: (t) => <Badge color={(t.status || "").toLowerCase() === "approved" ? "green" : "amber"}>{t.status || "draft"}</Badge> },
          { key: "version", header: "Ver", render: (t) => <span className="text-xs tabular-nums">v{t.version || 0}</span> },
          { key: "actions", header: "", render: (t) => (t.status || "").toLowerCase() !== "approved" ? (canApprove ? <button onClick={() => approve(t)} className="inline-flex items-center gap-1 text-xs text-teal-600 hover:underline" data-testid={`approve-term-${t.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Approve</button> : <span className="text-xs text-muted-foreground">—</span>) : <span className="text-xs text-emerald-600">✓</span> },
        ]} />

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="add-term-modal">
          <div className="bg-card rounded-xl border border-border w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground">Add business term</h3>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Term (e.g. Customer 360)" value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="term-input" />
              <input placeholder="Category (optional)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={form.doNotTranslate} onChange={(e) => setForm({ ...form, doNotTranslate: e.target.checked })} data-testid="term-lock-checkbox" />
                Keep verbatim across all languages (do not translate)
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowForm(false)} className="rounded-lg border border-border px-3 py-1.5 text-sm">Cancel</button>
              <button onClick={create} className="rounded-lg bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 text-sm" data-testid="submit-term-btn">Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
